import {
    Column,
    Container,
    DropDown,
    Grid,
    Input,
    Label,
    Row,
    SecondaryButton,
    toPascalCase,
    useAlignments,
    useScreen,
    useSizes,
    ZoomIcon,
} from 'metrix-common3';
import { useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import styled from 'styled-components';

const IconContainer = styled(Container)`
  cursor: pointer;
`;

const SearchHeader = (props) => {
    const alignments = useAlignments();
    const sizes = useSizes();
    const screen = useScreen();
    const intl = useIntl();

    const [displaySearchCriteria, setDisplaySearchCriteria] = useState(false);

    // biome-ignore lint/correctness/useExhaustiveDependencies: mount-only init
    useEffect(() => {
        if (displaySearchCriteria) {
            props.doQueryBoundPolicies();
        }
    }, []);

    const getFilterText = () => {
        const filterText = [];
        const query = props.query.bound;

        if (query.brokerReferenceGuid) {
            const assignees = getAssignees();
            filterText.push(
                `[b]${intl.formatMessage({ id: 'Policies_Bound.search.assignedTo' })}[b]: ${assignees.find((x) => x.code === query.brokerReferenceGuid).value}`,
            );
        }

        if (query.product) {
            const productName = props.products.find((product) => product.code === query.product).name;
            filterText.push(`[b]${intl.formatMessage({ id: 'Policies_Bound.table.product' })}[b]: ${productName}`);
        }

        if (query.status) {
            filterText.push(
                `[b]${intl.formatMessage({ id: 'Policies_Bound.table.status' })}[b]: ${toPascalCase(query.status)}`,
            );
        }

        if (query.policyTransactionType) {
            const displayedType = query.policyTransactionType;
            filterText.push(
                `[b]${intl.formatMessage({ id: 'Policies_Bound.table.type' })}[b]: ${toPascalCase(displayedType)}`,
            );
        }

        if (query.clientFirstName) {
            filterText.push(
                `[b]${intl.formatMessage({ id: 'Policies_Bound.table.fname' })}[b]: ${query.clientFirstName}`,
            );
        }

        if (query.clientLastName) {
            filterText.push(
                `[b]${intl.formatMessage({ id: 'Policies_Bound.table.lname' })}[b]: ${query.clientLastName}`,
            );
        }

        if (query.clientPhoneFullNumber) {
            filterText.push(
                `[b]${intl.formatMessage({ id: 'Policies_Bound.search.phone' })}[b]: ${query.clientPhoneFullNumber}`,
            );
        }

        if (query.clientEmail) {
            filterText.push(`[b]${intl.formatMessage({ id: 'Policies_Bound.search.email' })}[b]: ${query.clientEmail}`);
        }

        return filterText.join(', ');
    };

    const getAssignees = () => {
        const assignees = [
            {
                code: null,
                value: intl.formatMessage({ id: 'Policies_Bound.search.allBrokers' }),
            },
        ];

        if (props.reference?.references?.ActiveBrokers) {
            props.reference.references.ActiveBrokers.forEach((activeBroker) => {
                assignees.push({ code: activeBroker.code, value: activeBroker.value });
            });
        }

        return assignees;
    };

    const getProducts = () => {
        const products = [
            {
                code: null,
                value: intl.formatMessage({ id: 'Policies_Bound.search.allProducts' }),
            },
        ];

        if (props.products) {
            props.products.forEach((product) => {
                products.push({ code: product.code, value: product.name });
            });
        }

        return products;
    };

    const query = props.query.bound;

    // Get translated texts for inputs and buttons
    const assignedToText = intl.formatMessage({ id: 'Policies_Bound.search.assignedTo' });
    const phoneText = intl.formatMessage({ id: 'Policies_Bound.search.phone' });
    const emailText = intl.formatMessage({ id: 'Policies_Bound.search.email' });
    const clearText = intl.formatMessage({ id: 'Policies_Bound.search.clear' });

    return (
        <Container>
            <Container top={sizes.xs} right={sizes.xs} left={sizes.xs}>
                <Container top={sizes.none} inline verticalAlignment={alignments.center}>
                    <Grid>
                        <Row>
                            <Column xs={11} nogutter>
                                <Label text={getFilterText()} textAlign={alignments.left} />
                            </Column>
                            <Column xs={1}>
                                <Container fullSize horizontalAlignment={alignments.right}>
                                    <IconContainer clickEvent={() => setDisplaySearchCriteria(!displaySearchCriteria)}>
                                        <ZoomIcon size={sizes.sm} />
                                    </IconContainer>
                                </Container>
                            </Column>
                        </Row>
                    </Grid>
                </Container>

                {displaySearchCriteria && (
                    <Container top={sizes.none} bottom={screen?.isMobile && sizes.xs}>
                        <Grid>
                            <Row nogutter>
                                <Column xs={0} md={0} lg={2} />
                                <Column xs={12} md={12}>
                                    <Grid>
                                        <Row nogutter>
                                            <Column xs={6} md={3}>
                                                <DropDown
                                                    top={sizes.xs}
                                                    placeholder={assignedToText}
                                                    value={{
                                                        code:
                                                            query.brokerReferenceGuid == null
                                                                ? null
                                                                : query.brokerReferenceGuid,
                                                    }}
                                                    references={getAssignees()}
                                                    referencesEvent={() =>
                                                        props.reference.doGetReferences('ActiveBrokers')
                                                    }
                                                    changeEvent={(value) =>
                                                        props.doReduceQueryElement({
                                                            code: 'brokerReferenceGuid',
                                                            value: value.code,
                                                        })
                                                    }
                                                    allowEdit
                                                />
                                            </Column>
                                            <Column xs={6} md={3}>
                                                <DropDown
                                                    top={sizes.xs}
                                                    placeholder={intl.formatMessage({
                                                        id: 'Policies_Bound.table.product',
                                                    })}
                                                    value={{ code: query.product == null ? null : query.product }}
                                                    references={getProducts()}
                                                    // biome-ignore lint/suspicious/noEmptyBlockStatements: noop callback for referencesEvent contract
                                                    referencesEvent={() => {}}
                                                    changeEvent={(value) =>
                                                        props.doReduceQueryElement({
                                                            code: 'product',
                                                            value: value.code,
                                                        })
                                                    }
                                                    allowEdit
                                                />
                                            </Column>
                                            <Column xs={6} md={3}>
                                                <Input
                                                    top={sizes.xs}
                                                    inverse
                                                    placeholder={intl.formatMessage({
                                                        id: 'Policies_Bound.table.fname',
                                                    })}
                                                    value={query.clientFirstName}
                                                    changeEvent={(value) => {
                                                        props.doReduceQueryElement({
                                                            code: 'clientFirstName',
                                                            value,
                                                        });
                                                    }}
                                                />
                                            </Column>
                                            <Column xs={6} md={3}>
                                                <Input
                                                    top={sizes.xs}
                                                    inverse
                                                    placeholder={intl.formatMessage({
                                                        id: 'Policies_Bound.table.lname',
                                                    })}
                                                    value={query.clientLastName}
                                                    changeEvent={(value) => {
                                                        props.doReduceQueryElement({
                                                            code: 'clientLastName',
                                                            value,
                                                        });
                                                    }}
                                                />
                                            </Column>
                                            <Column xs={6} md={3}>
                                                <Input
                                                    top={sizes.xs}
                                                    inverse
                                                    placeholder={phoneText}
                                                    value={query.clientPhoneFullNumber}
                                                    changeEvent={(value) => {
                                                        props.doReduceQueryElement({
                                                            code: 'clientPhoneFullNumber',
                                                            value,
                                                        });
                                                    }}
                                                />
                                            </Column>
                                            <Column xs={6} md={3}>
                                                <Input
                                                    top={sizes.xs}
                                                    inverse
                                                    placeholder={emailText}
                                                    value={query.clientEmail}
                                                    changeEvent={(value) => {
                                                        props.doReduceQueryElement({
                                                            code: 'clientEmail',
                                                            value,
                                                        });
                                                    }}
                                                />
                                            </Column>
                                            <Column xs={6} md={12} lg={12}>
                                                <Container
                                                    inline
                                                    fullSize
                                                    top={sizes.xs}
                                                    horizontalAlignment={alignments.center}
                                                >
                                                    <SecondaryButton
                                                        text={<FormattedMessage id='Policies_Bound.table.search' />}
                                                        bottom={sizes.xs}
                                                        clickEvent={() => {
                                                            setDisplaySearchCriteria(false);
                                                            props.doReduceQueryElement({
                                                                code: 'search',
                                                                value: true,
                                                            });
                                                            props.doQueryBoundPolicies();
                                                        }}
                                                    />
                                                    <SecondaryButton
                                                        left={sizes.xs}
                                                        bottom={sizes.xs}
                                                        text={clearText}
                                                        clickEvent={() => {
                                                            setDisplaySearchCriteria(false);
                                                            props.doReduceQueryElement({
                                                                code: 'search',
                                                                value: false,
                                                            });
                                                            props.doGetBoundPolicies();
                                                        }}
                                                    />
                                                </Container>
                                            </Column>
                                        </Row>
                                    </Grid>
                                </Column>
                                <Column xs={0} md={0} lg={2} />
                            </Row>
                        </Grid>
                    </Container>
                )}
            </Container>
        </Container>
    );
};

export default SearchHeader;
